import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import { RovoIcon } from '@atlaskit/logo';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { token } from '@atlaskit/tokens';

export const RovoInput = ({ onSend }: { onSend: (message: string) => void }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [value, setValue] = useState('');

	const handleSend = useCallback(() => {
		if (value.trim()) {
			onSend(value.trim());
			setValue('');
		}
	}, [value, onSend]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend],
	);

	return (
		<div css={styles.rovoInputContainer}>
			<div css={styles.rovoIcon}>
				<RovoIcon size="xsmall" appearance="brand" />
			</div>
			<input
				type="text"
				css={styles.rovoInput}
				placeholder={formatMessage(i18n.askRovoPlaceholder)}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={handleKeyDown}
			/>
			<IconButton
				icon={ArrowUpIcon}
				label={formatMessage(i18n.sendQuestion)}
				appearance="primary"
				shape="circle"
				isDisabled={!value.trim()}
				onClick={handleSend}
			/>
		</div>
	);
};

const i18n = defineMessages({
	askRovoPlaceholder: {
		id: 'space-overview.smart-space-overview.popular-questions.ask-rovo-placeholder',
		defaultMessage: 'Ask Rovo anything',
		description: 'Placeholder text for the Rovo question input.',
	},
	sendQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.send-question',
		defaultMessage: 'Send question',
		description: 'Accessible label for the send question button.',
	},
});

const styles = cssMap({
	rovoInputContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
	},
	rovoIcon: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	rovoInput: {
		flex: 1,
		borderWidth: '0',
		outline: 'none',
		backgroundColor: 'transparent',
		font: token('font.body'),
		color: token('color.text'),
		'&::placeholder': {
			color: token('color.text.subtlest'),
		},
	},
});
